import { toast } from '../../../../components/ui/toaster';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import type { FilterGroup, SortDefinition } from '../../core/types/workspaceTypes';
import { engineSupports, isValidEngineId } from '../../../../core/engines';
import { tableExplorerUnsupportedReason } from '../../../main-toolbar/model/toolbarEngineFilters';
import type { UserFacingEngineId } from '../../../database-blueprint/types/databaseBlueprintTypes';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

export function openTableExplorerTab(args: {
  readonly connectionId: string;
  readonly database: string;
  readonly schema: string;
  readonly tableName: string;
  readonly title?: string;
  readonly limit?: number;
  readonly selectedColumns?: string[] | '*';
  readonly filters?: FilterGroup[];
  readonly sorting?: SortDefinition[];
}): void {
  const {
    connectionId,
    database,
    schema,
    tableName,
    title,
    limit = 100,
    selectedColumns = '*',
    filters = [],
    sorting = [],
  } = args;

  const { connections, activeConnections } = useConnectionStore.getState();
  const savedConnection = connections.find((c) => c.id === connectionId);
  const activeConnection = activeConnections.find((c) => c.id === connectionId);

  // For browser mode SQLite, there may be no savedConnection - only activeConnection
  if (!activeConnection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  // Use savedConnection if available, otherwise fall back to activeConnection
  const connectionConfig = savedConnection || activeConnection.connection;
  const rawEngine = connectionConfig.engine ?? 'mssql';
  const engine = isValidEngineId(rawEngine) ? rawEngine : 'mssql';

  // Defensive capability gate (S1 Task 1.16). Every caller has its own gate;
  // this one exists because the opener is a `void` fire-and-forget reached
  // from a dozen sites. It reports the refusal the way the sibling guard above
  // reports a missing connection (toast + return) rather than throwing into a
  // click handler nobody wraps.
  if (!engineSupports(engine, 'workspaceFeatures', 'supportsTableExplorer')) {
    toast.error({
      title: 'Table Explorer unavailable',
      description: tableExplorerUnsupportedReason(engine),
    });
    return;
  }

  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';

  const resolvedSchema = schema.trim() ? schema : defaultSchema;
  const resolvedTitle = title || (resolvedSchema ? `${resolvedSchema}.${tableName}` : tableName);

  useWorkspaceStore.getState().addTab({
    title: resolvedTitle,
    type: 'table-explorer',
    content: {
      tableName,
      schema: resolvedSchema,
      database,
      rows: [],
      columns: [],
      limit,
      selectedColumns,
      filters,
      sorting,
      isLoading: true,
    },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connectionConfig, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database,
    },
  });
}

/**
 * Open a Table Explorer tab in "Blueprint edit" mode — the grid renders the
 * rows captured in a Database Blueprint's `Data/<schema>.<table>.data.json`
 * sidecar (NOT a live-DB query), and Save writes back to the sidecar +
 * regenerates the `.data.sql` upsert script. The linked DB's
 * `connectionId`/`database` are carried through so MetaInfo (JSON/enum/
 * loose-FK overlays) and the table's column type pickers keep working.
 *
 * Unlike {@link openTableExplorerTab}, this never requires an active
 * connection: a blueprint may be linked to a DB that isn't currently
 * connected (or whose connection was removed). In that case MetaInfo is
 * simply empty and editing still proceeds against the sidecar.
 */
export function openBlueprintTableExplorerTab(args: {
  readonly folderPath: string;
  readonly schema: string;
  readonly name: string;
  /** Linked-DB connection id (may be empty / point at a disconnected DB). */
  readonly connectionId: string;
  /** Linked-DB database name (used for MetaInfo lookup). */
  readonly database: string;
  readonly engine: UserFacingEngineId;
}): void {
  const { folderPath, schema, name, connectionId, database, engine } = args;

  const { connections } = useConnectionStore.getState();
  const savedConnection = connections.find((c) => c.id === connectionId);
  const connectionName = safeConnectionAlias(savedConnection, 'Database Blueprint');
  const serverHost = safeConnectionServer(savedConnection);

  const resolvedTitle = schema ? `${schema}.${name} (blueprint)` : `${name} (blueprint)`;

  useWorkspaceStore.getState().addTab({
    title: resolvedTitle,
    type: 'table-explorer',
    content: {
      tableName: name,
      schema,
      database,
      rows: [],
      columns: [],
      limit: 100,
      selectedColumns: '*',
      filters: [],
      sorting: [],
      isLoading: true,
      // Blueprint edit mode: render from the sidecar, save to the sidecar.
      isEditMode: true,
      blueprintEdit: { folderPath, engine },
    },
    connectionInfo: {
      connectionId,
      connectionName,
      serverHost,
      database,
    },
  });
}
